import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  getCurrentSession,
  getCurrentUser,
  signInWithGoogle,
  signInWithPassword,
  signUp,
  signOut,
  refreshSession,
  onAuthStateChange,
  getUserProfile
} from '../utils/authUtils';
import supabase from '../utils/supabaseClient';
import { apiPost } from '../utils/apiUtils';

// Create the context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);

  // Impersonation state - initialize conservatively (will be validated in useEffect)
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUserEmail, setImpersonatedUserEmail] = useState(null);

  // Track visibility state to prevent extra auth checks
  // Using a ref instead of state to avoid triggering re-renders
  const lastVisibilityCheckRef = useRef(Date.now());

  // Handle auth state changes - but only for actual auth events, not visibility changes
  const handleAuthChange = useCallback(async (event, session) => {
    // Avoid unnecessary checks - don't reload on just visibility changes
    if (event === 'VISIBILITY_CHANGE') {
      // Just update the ref without triggering re-renders
      lastVisibilityCheckRef.current = Date.now();
      return;
    }

    // Don't set loading for background token refresh to prevent unmounting components
    // Only set loading for user-initiated auth operations (SIGNED_IN, SIGNED_OUT)
    const isBackgroundRefresh = event === 'TOKEN_REFRESHED';
    if (!isBackgroundRefresh) {
      setLoading(true);
    }

    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      try {
        // Get current user data
        const { user: currentUser, error: userError } = await getCurrentUser();

        if (userError) {
          console.error('Error fetching user after auth change:', userError);
          setError(userError);
          if (!isBackgroundRefresh) {
            setLoading(false);
          }
          return;
        }

        setUser(currentUser);
        setSession(session);

        // Fetch user profile data if available
        if (currentUser?.id) {
          const { profile, error: profileError } = await getUserProfile(currentUser.id);
          if (!profileError) {
            setUserProfile(profile);
          }
        }
      } catch (err) {
        console.error('Error handling auth state change:', err);
        setError(err);
      }
    } else if (event === 'SIGNED_OUT') {
      setUser(null);
      setSession(null);
      setUserProfile(null);

      // Clear impersonation state
      setIsImpersonating(false);
      setImpersonatedUserEmail(null);
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUserEmail');
      localStorage.removeItem('adminSession');
    }

    if (!isBackgroundRefresh) {
      setLoading(false);
    }
  }, []);

  // Validate impersonation state from localStorage on mount
  useEffect(() => {
    const storedIsImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const storedEmail = localStorage.getItem('impersonatedUserEmail');
    const storedAdminSession = localStorage.getItem('adminSession');

    // Only set impersonating if we have ALL required data
    if (storedIsImpersonating && storedEmail && storedAdminSession) {
      setIsImpersonating(true);
      setImpersonatedUserEmail(storedEmail);
    } else if (storedIsImpersonating) {
      // Partial state - clear it
      console.log('[Auth] Clearing partial impersonation state');
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUserEmail');
      localStorage.removeItem('adminSession');
    }
  }, []); // Run once on mount

  // Check for existing session on mount and set up auth change listener
  // But avoid frequent rechecks that cause UI elements to collapse
  useEffect(() => {
    // Add a visibility change listener that DOESN'T trigger auth checks
    const handleVisibilityChange = () => {
      // Just mark that we had a visibility change without triggering re-renders
      // This prevents the full auth check when just switching tabs
      lastVisibilityCheckRef.current = Date.now();
    };
    
    // Add visibility listener without triggering auth checks
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const checkAuth = async () => {
      try {
        setLoading(true);
        const { session: currentSession, error: sessionError } = await getCurrentSession();
        
        if (sessionError) {
          console.error('Error checking session:', sessionError);
          setError(sessionError);
          setLoading(false);
          return;
        }
        
        if (currentSession) {
          setSession(currentSession);
          
          // If we have a session, get the user details
          const { user: currentUser, error: userError } = await getCurrentUser();
          
          if (userError) {
            console.error('Error fetching user:', userError);
            setError(userError);
            setLoading(false);
            return;
          }
          
          setUser(currentUser);

          // Detect and clear stale impersonation state
          const storedIsImpersonating = localStorage.getItem('isImpersonating') === 'true';
          const storedAdminSession = localStorage.getItem('adminSession');

          if (storedIsImpersonating && currentUser) {
            // We have impersonation flag but are logged in - validate adminSession
            let isValidImpersonation = false;

            if (storedAdminSession) {
              try {
                const adminSessionData = JSON.parse(storedAdminSession);
                if (adminSessionData.access_token && adminSessionData.refresh_token) {
                  isValidImpersonation = true;
                }
              } catch (e) {
                console.error('Invalid adminSession in localStorage:', e);
              }
            }

            // If we're "impersonating" but have no valid admin session to return to,
            // this is stale state from a previous session - clear it
            if (!isValidImpersonation) {
              console.log('[Auth] Clearing stale impersonation state');
              localStorage.removeItem('isImpersonating');
              localStorage.removeItem('impersonatedUserEmail');
              localStorage.removeItem('adminSession');
              setIsImpersonating(false);
              setImpersonatedUserEmail(null);
            }
          }

          // Fetch user profile data if available
          if (currentUser?.id) {
            const { profile, error: profileError } = await getUserProfile(currentUser.id);
            if (!profileError) {
              setUserProfile(profile);
            }
          }
        }
      } catch (err) {
        console.error('Unexpected error during auth check:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial auth check
    checkAuth();
    
    // Set up auth state change listener
    const { data: authListener } = onAuthStateChange(handleAuthChange);
    
    // Clean up function
    return () => {
      if (authListener) {
        authListener.subscription.unsubscribe();
      }
      
      // Remove visibility listener
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleAuthChange]);

  // Function to manually refresh the token
  const handleRefreshSession = async () => {
    try {
      setLoading(true);
      const { session: refreshedSession, user: refreshedUser, error: refreshError } = await refreshSession();
      
      if (refreshError) {
        console.error('Error refreshing session:', refreshError);
        setError(refreshError);
        return { success: false, error: refreshError };
      }
      
      if (refreshedSession) {
        setSession(refreshedSession);
        setUser(refreshedUser);
        return { success: true };
      }
      
      return { success: false, error: new Error('Session refresh did not return session') };
    } catch (err) {
      console.error('Unexpected error during session refresh:', err);
      setError(err);
      return { success: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  // Impersonate user function
  const impersonateUser = async (userId) => {
    try {
      console.log('[Impersonation] Starting impersonation for user:', userId);
      setLoading(true);

      // Get current admin session before switching
      const { data: adminSessionData } = await supabase.auth.getSession();
      const adminSession = adminSessionData.session;

      if (!adminSession) {
        throw new Error('No admin session found');
      }

      // Store admin session and user info
      localStorage.setItem('adminSession', JSON.stringify({
        access_token: adminSession.access_token,
        refresh_token: adminSession.refresh_token,
        user: adminSession.user
      }));

      console.log('[Impersonation] Admin session stored');

      // Call backend to generate magic link token
      // Use relative URL to work in both development and production
      const response = await apiPost(`/api/usage/admin/impersonate/${userId}`);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate impersonation token');
      }

      const { data } = await response.json();
      console.log('[Impersonation] Magic link token received');

      // Store impersonation state
      localStorage.setItem('isImpersonating', 'true');
      localStorage.setItem('impersonatedUserEmail', data.email);

      // Verify OTP to create user session
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: data.token,
        type: 'magiclink'
      });

      if (verifyError) {
        // Clean up on error
        localStorage.removeItem('isImpersonating');
        localStorage.removeItem('impersonatedUserEmail');
        throw verifyError;
      }

      console.log('[Impersonation] User session created, reloading...');

      // Reload to apply new session
      window.location.reload();

    } catch (error) {
      console.error('[Impersonation] Error:', error);
      setError(error.message || 'Failed to impersonate user');
      setLoading(false);
      throw error;
    }
  };

  // Exit impersonation function
  const exitImpersonation = async () => {
    try {
      console.log('[Impersonation] Exiting impersonation mode');
      setLoading(true);

      // Retrieve stored admin session
      const adminSessionStr = localStorage.getItem('adminSession');

      // Clear localStorage FIRST to prevent infinite loops
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUserEmail');
      localStorage.removeItem('adminSession');
      setIsImpersonating(false);
      setImpersonatedUserEmail(null);

      if (!adminSessionStr) {
        // No admin session to restore - just sign out current user
        console.log('[Impersonation] No admin session found, signing out');
        await signOut();
        window.location.reload();
        return;
      }

      let adminSession;
      try {
        adminSession = JSON.parse(adminSessionStr);
      } catch (e) {
        console.error('[Impersonation] Invalid adminSession JSON:', e);
        await signOut();
        window.location.reload();
        return;
      }

      // Try to restore admin session
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: adminSession.access_token,
        refresh_token: adminSession.refresh_token
      });

      if (sessionError) {
        console.error('[Impersonation] Failed to restore admin session:', sessionError);
        // Don't throw - just sign out and reload
        await signOut();
      }

      console.log('[Impersonation] Admin session restored, reloading...');
      window.location.reload();

    } catch (error) {
      console.error('[Impersonation] Error exiting:', error);

      // Always clear state even on error (safety net)
      localStorage.removeItem('isImpersonating');
      localStorage.removeItem('impersonatedUserEmail');
      localStorage.removeItem('adminSession');
      setIsImpersonating(false);
      setImpersonatedUserEmail(null);

      // Sign out and reload to get to clean state
      await signOut();
      window.location.reload();
    }
  };

  // The value to be provided to consumers of this context
  const value = {
    user,
    userProfile,
    session,
    loading,
    error,
    signInWithGoogle,
    signInWithPassword,
    signUp,
    signOut,
    refreshSession: handleRefreshSession,
    isAuthenticated: !!session,
    isImpersonating,
    impersonatedUserEmail,
    impersonateUser,
    exitImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
